/* eslint-disable no-constant-condition */
/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
// constants
import { isArray } from 'lodash';
import {
    reportLeadStatuses,
    treatmentCategory,
    isLeadArchived,
    leadSourceType,
    leadSourceReferral,
    AllLeadData,
    AllTreatments,
    categorySummary,
    sourceTypeSummary,
    referralSummary,
    archiveSummary
} from '../../constants/ReportsLeadConst';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import { errorHandler } from '../../utils/errorHandler';
import { leadTableFields } from '../../constants/fields';
// store
import { dispatch } from '../store';
import { getPracticeId } from './clincheck';
import { setCounter } from './widgetCounter';

// ------------------------------------------------------------------------

const initialState = {
    isLoading: true,
    AllTargets: null,
    analytics: null,
    treatmentSummary: null,
    targetData: null,
    consultationTarget: null,
    startedTarget: null,
    targetTreatment: null,
    startTime: moment(new Date()).startOf('month').format('YYYY-MM-DD 00:00:00'),
    endTime: moment(new Date()).format('YYYY-MM-DD 23:59:00'),
    doctorsList: null,
    patientList: null,
    statusesReports: null,
    treatmentCategoryReports: null,
    archivedLeadReport: null,
    leadSourceTypeReports: null,
    sourceReferral: null,
    userLeads: null,
    reportsAllLead: 0,
    reportsAllTreatments: 0,
    categorySummaryData: null,
    enquiryColumnData: null,
    notBookedColumnData: null,
    reportStartTime: null,
    reportEndTime: null,
    sourceTypeSummaryData: null,
    referralSummaryData: null,
    archiveSummaryData: null,
    categoryArchivedData: null,
    treatmentArchivedData: null,
    sourceArchivedData: null,
    referralArchivedData: null,
    ScheduleSettingList: {},
    // Past CSV reports already generated by the scheduler — populated
    // by getGeneratedReports() per-schedule. Keyed by schedule id so the
    // Past Sends dialog can be opened for one schedule without clearing
    // another's data on close.
    generatedReports: {},
    // True while a test-send is in flight — disables the Send Test
    // button to prevent double-fires (rate-limited at 10/hour BE-side,
    // but UX shouldn't let a user spam the button).
    isTestSending: false,
    leads: {
        results: []
    },
    dateRange: {
        startDate: moment(new Date()).startOf('month').format('YYYY-MM-DD'),
        endDate: moment(new Date()).format('YYYY-MM-DD')
    },
    treatmentStartedCount: 0,
    reportAppointmentData: null,
    reportsLoading: false,
    leadMonthlyStatus: null
};

const slice = createSlice({
    name: 'analytics',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        // START LOADING
        startLoading(state) {
            state.isLoading = false;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        addAnalytics(state, action) {
            state.analytics = action.payload;
        },
        addTreatmentSummery(state, action) {
            state.isLoading = false;
            state.treatmentSummary = action.payload;
        },
        clearTreatmentSummary(state) {
            state.treatmentSummary = null;
        },
        addTargetDataSlice(state, action) {
            state.targetData = action.payload;
        },
        addAllTargets(state, action) {
            state.AllTargets = action.payload;
        },
        consultationTargetSlice(state, action) {
            state.consultationTarget = {
                ...state?.consultationTarget,
                ...action.payload
            };
        },
        startedTargetSlice(state, action) {
            state.startedTarget = {
                ...state?.startedTarget,
                ...action.payload
            };
        },
        addTargetTreatment(state, action) {
            state.targetTreatment = action.payload;
        },
        addStartTime(state, action) {
            state.startTime = action.payload;
        },
        addEndTime(state, action) {
            state.endTime = action.payload;
        },
        addDoctorslist(state, action) {
            state.doctorsList = action.payload;
        },
        addPatientList(state, action) {
            state.patientList = action.payload;
        },
        clearPatientList(state) {
            state.patientList = null;
        },
        addReportsLeadStatuses(state, action) {
            state.statusesReports = action.payload;
        },
        clearReportsLeadStatuses(state) {
            state.statusesReports = null;
        },
        clearReportsAppointments(state) {
            state.reportAppointmentData = null;
        },
        addTreatmentCategoryReport(state, action) {
            state.treatmentCategoryReports = action.payload;
        },
        clearTreatmentCategoryReport(state) {
            state.treatmentCategoryReports = null;
        },
        addArchivedLeadReport(state, action) {
            state.archivedLeadReport = action.payload;
        },
        addleadSourceType(state, action) {
            state.leadSourceTypeReports = action.payload;
        },
        clearleadSourceType(state) {
            state.leadSourceTypeReports = null;
        },
        addleadSourceReferral(state, action) {
            state.sourceReferral = action.payload;
        },
        clearleadSourceReferral(state) {
            state.sourceReferral = null;
        },
        addReportsAllLead(state, action) {
            state.reportsAllLead = action.payload;
        },
        addReportsAllTreatments(state, action) {
            state.reportsAllTreatments = action.payload;
        },
        addUserLeads(state, action) {
            state.userLeads = action.payload;
        },
        clearUserLeads(state) {
            state.userLeads = null;
        },
        addCategorySummary(state, action) {
            state.categorySummaryData = action.payload;
        },
        addEnquiryColumnData(state, action) {
            state.enquiryColumnData = action.payload;
        },
        addReportAppointmentData(state, action) {
            state.reportAppointmentData = action.payload;
        },
        addNotBookedColumnData(state, action) {
            state.notBookedColumnData = action.payload;
        },
        addReportStartTime(state, action) {
            state.reportStartTime = action.payload;
        },
        addReportEndTime(state, action) {
            state.reportEndTime = action.payload;
        },
        addSourceTypeSummary(state, action) {
            state.sourceTypeSummaryData = action.payload;
        },
        addReferralSummary(state, action) {
            state.referralSummaryData = action.payload;
        },
        addArchiveSummary(state, action) {
            state.archiveSummaryData = action.payload;
        },
        archivedCategoryData(state, action) {
            state.categoryArchivedData = action.payload;
        },
        archivedTreatmentCategory(state, action) {
            state.treatmentArchivedData = action.payload;
        },
        archivedCategory(state, action) {
            state.treatmentArchivedData = action.payload;
        },
        archivedSourceCategory(state, action) {
            state.sourceArchivedData = action.payload;
        },
        archivedReferralCategory(state, action) {
            state.referralArchivedData = action.payload;
        },
        getScheduleSettingList(state, action) {
            state.ScheduleSettingList = action.payload;
        },
        setGeneratedReports(state, action) {
            // payload: { scheduleId, data }
            const { scheduleId, data } = action.payload;
            state.generatedReports = { ...state.generatedReports, [scheduleId]: data };
        },
        setIsTestSending(state, action) {
            state.isTestSending = action.payload;
        },
        getLeadList(state, action) {
            state.leads = action.payload;
        },
        setDateRange(state, action) {
            state.dateRange = action.payload;
        },
        clearPopupLead(state) {
            state.leads = {
                results: []
            };
        },
        treatmentStartedReport(state, action) {
            state.treatmentStartedCount = action.payload;
        },
        setReportsLoading(state, action) {
            state.reportsLoading = action.payload;
        },
        addLeadMonthlyStatus(state, action) {
            state.leadMonthlyStatus = action.payload;
        }
    }
});

export default slice.reducer;

export const {
    addAnalytics,
    addConsultationTarget,
    addGoAheadTarget,
    addTreatment,
    addTargetTreatment,
    addStartTime,
    addEndTime,
    clearTreatmentSummary,
    clearUserLeads,
    clearleadSourceReferral,
    clearleadSourceType,
    clearReportsLeadStatuses,
    clearReportsAppointments,
    clearTreatmentCategoryReport,
    clearPatientList,
    addReportStartTime,
    addReportEndTime,
    archivedCategoryData,
    archivedTreatmentCategory,
    archivedReferralCategory,
    archivedSourceCategory,
    getScheduleSettingList,
    getLeadList,
    reset,
    setDateRange,
    clearPopupLead,
    addEnquiryColumnData,
    addNotBookedColumnData,
    setReportsLoading
} = slice.actions;

export function getAnalytics(practiceId, id, after, before) {
    return async () => {
        try {
            const treatment = id ? `?treatment=${id}` : '';
            const afterParam = after ? `${id ? '&' : '?'}date_after=${after}` : '';
            const beforeParam = before ? `${id || after ? '&' : '?'}date_before=${before}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-statistics/${treatment}${afterParam}${beforeParam}`);
            if (response.status === 200) {
                dispatch(slice.actions.addAnalytics(response.data));
            }
            else {
                errorHandler(response.data, '');
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function getTreatmentSummary(practiceId, id, after, before) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const treatment = id ? `?treatment=${id}` : '';
            const afterParam = after ? `${id ? '&' : '?'}time_after=${after}` : '';
            const beforeParam = before ? `${id || after ? '&' : '?'}time_before=${before}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-treatment-statistics/${treatment}${afterParam}${beforeParam}`);
            if (response.status === 200) {
                dispatch(slice.actions.addTreatmentSummery(response.data));
            }
            else {
                errorHandler(response.data, '');
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function getEventBasedAnalytics(practiceId, dataSetterCb) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/analytics-events/?name=practice_phone_number_clicked&ordering=-created_at`);
            if (response.status === 200) {
                dataSetterCb(response?.data?.results);
            } else {
                dataSetterCb([]);
            }
        } catch (error) {
            console.error("Error while fetching data for event based analytics");
            dataSetterCb([])
        }
    }
}

export function getWhatsAppEventBasedAnalytics(practiceId, dataSetterCb) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/analytics-events/?name=practice_whatsapp_button_clicked&ordering=-created_at`);
            if (response.status === 200) {
                dataSetterCb(response?.data?.results);
            } else {
                dataSetterCb([]);
            }
        } catch (error) {
            console.error("Error while fetching data for whatsapp event based analytics");
            dataSetterCb([])
        }
    }
}

export function getPracticeTwilioPhones(practiceId, dataSetterCb) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/`);

            if (response.status !== 200) {
                dataSetterCb([]);
                return;
            }

            const phoneIds = response?.data?.results.map(ele => ele.id);

            dataSetterCb(phoneIds);

        } catch (error) {
            console.error("Error while fetching data for event-based analytics", error);
            dataSetterCb([]);
        }
    };
}

export function getPracticeTwilioPhoneCallDetails(practiceId, phoneIds, dataSetterCb, countSetterCb, limit, offset, ordering, startDate, endDate) {
    return async () => {
        try {
            if(phoneIds && phoneIds.length){
                const callDataResponses = await Promise.all(
                    phoneIds.map(phoneId => 
                        callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/?limit=${limit}&offset=${offset}${ordering ? `&ordering=${ordering}` : ''}${startDate ? `&updated_at__gte=${startDate}` : ''}${endDate ? `&updated_at__lte=${endDate}` : ''}`)
                        .then(res => {
                            if (res.status === 200) {
                                return { results: res.data?.results || [], count: res.data?.count || 0 };
                            }
                            return { results: [], count: 0 };
                        })
                        .catch(err => {
                            console.error(`Error fetching call data for phoneId ${phoneId}:`, err);
                            return { results: [], count: 0 };
                        })
                    )
                );
    
                const finalData = callDataResponses.flatMap(data => data.results);
                const totalCounts = callDataResponses.reduce((acc, curr) => acc + curr.count, 0);
                
                dataSetterCb(finalData);
                countSetterCb(totalCounts);
            }
        } catch (error) {
            console.error("Error while fetching data for event-based analytics", error);
            dataSetterCb([]);
        }
    };
}

export function getTargetData(practiceId, id) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/treatments/${id}/targets/`);
            if (response.status === 200) {
                dispatch(slice.actions.addTargetDataSlice(response.data.results));
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function addTargetData(data, practiceId, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/treatments/${id}/targets/`, 'post', data);
            if (response.status === 201) {
                if (response?.data?.lead_status === 'CONSULTATION_BOOKED') {
                    dispatch(slice.actions.consultationTargetSlice(response?.data));
                }
                if (response?.data?.lead_status === 'TREATMENT_STARTED') {
                    dispatch(slice.actions.startedTargetSlice(response?.data));
                }
                handleSuccess({ success: true, message: 'Target Added successfully' });
            }
            else {
                errorHandler(response.data, '', updateError);
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function removeTargetData(practiceId, treatment, id, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/treatments/${treatment}/targets/${id}/`, 'delete');
            if (response.status === 204) {
                handleSuccess({ success: true, message: 'Target deleted successfully' });
            }
            else {
                errorHandler(response.data, '', updateError);
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function getAllTargets(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/targets/`);
            if (response.status === 200) {
                dispatch(slice.actions.addAllTargets(response?.data || []));
            }
            else {
                errorHandler(response.data, '', updateError);
            }
        } catch (error) {
            errorHandler(error, '', updateError);
        }
    };
}

export function treatmentStartedReports(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const date = (data?.startTime && data?.endTime) ? `&state_transition_params={"state":"TREATMENT_STARTED","timestamp_gte":"${data?.startTime}","timestamp_lte":"${data?.endTime}"}` : '';
            const parameter = 'group_by[lead_treatments__state_transition_logs__state]&aggregate[Count]=id';
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/reports/leads/?${parameter}${date}`);
            if (response.status === 200) {
                dispatch(slice.actions.treatmentStartedReport(response?.data));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function reportsLeadListing(data, dataSetter) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const id = data?.treatment_id ? `&lead_treatments__treatment__in=${data?.treatment_id}` : '';
            const date = (data?.startTime && data?.endTime) ? data?.isCategoryTable ? `&state_transition_params={${data?.state ? `"state": "${data.state}",` : ''}"timestamp_gte":"${data?.startTime}","timestamp_lte":"${data?.endTime}"}` : `&created_at__gte=${data?.startTime}&created_at__lte=${data?.endTime}` : '';
            const Status = data?.reportStatus ? `&status__in=${data?.reportStatus}` : '';
            const categoryId = data?.categoryId ? `&lead_treatments__treatment__treatment_item__treatment_category=${data.categoryId}` : '';
            const referral = data?.referral ? `&lead_source__referral__in=${data.referral}` : '';
            const source = data?.source ? `&lead_source__type__in=${data.source}` : '';
            const isArchived = data?.isArchived && data?.archivedCategory ? `&is_archived=true&group_by[${encodeURIComponent(data.archivedCategory)}]` : '';
            const parameter = data?.type === 'totalLead' ? 'aggregate[Count]=id' :
                data?.type === 'totalTreatments' ? 'aggregate[Count]=lead_treatments' :
                    data?.type === 'categorySummary' || data?.type === 'referralSummary' || data?.type === 'sourceTypeSummary' ? (data.isArchived ? (`&aggregate[Count]=id${categoryId}${isArchived}${referral}${source}`) : (`group_by[${data?.category}]&group_by[${data?.categoryStatus}]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price${categoryId}${isArchived}${data?.type === 'categorySummary' && data?.toggleStartDate && data?.toggleEndDate ? `&created_at__gte=${data.toggleStartDate}&created_at__lte=${data.toggleEndDate}` : ''}`)) :
                        data?.type === 'archiveSummary' ? `group_by[${data?.category}]&aggregate[Count]=id&is_archived=true` :
                            (data.isArchived ? `&aggregate[Count]=id&${isArchived}&group_by[${data?.type}]` : `group_by[${data?.type}]&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price`);
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/reports/leads/?${parameter}${date}${id}${Status}`);
            if (response.status === 200) {
                if (data?.type === AllLeadData) {
                    dispatch(slice.actions.addReportsAllLead(response?.data));
                }
                if (data?.type === reportLeadStatuses) {
                    dispatch(slice.actions.addReportsLeadStatuses(response?.data));
                }
                if (data?.type === treatmentCategory) {
                    dispatch(slice.actions.addTreatmentCategoryReport(response?.data));
                }
                if (data?.type === isLeadArchived) {
                    dispatch(slice.actions.addArchivedLeadReport(response?.data));
                }
                if (data?.type === leadSourceType) {
                    dispatch(slice.actions.addleadSourceType(response?.data));
                }
                if (data?.type === leadSourceReferral && !data?.isArchived) {
                    dispatch(slice.actions.addleadSourceReferral(response?.data));
                }
                if (data?.type === leadSourceReferral && data?.isArchived) {
                    dispatch(slice.actions.archivedTreatmentCategory(response?.data));
                }
                if (data?.type === AllTreatments) {
                    dispatch(slice.actions.addReportsAllTreatments(response?.data));
                }
                if (data?.type === categorySummary && !data?.isArchived) {
                    if (dataSetter){
                        dataSetter(response?.data?.data || [])
                    }
                    dispatch(slice.actions.addCategorySummary(response?.data));
                }
                if (data?.type === categorySummary && data?.isArchived) {
                    dispatch(slice.actions.archivedCategoryData(response?.data));
                }
                if (data?.type === sourceTypeSummary && !data?.isArchived) {
                    dispatch(slice.actions.addSourceTypeSummary(response?.data));
                }
                if (data?.type === sourceTypeSummary && data?.isArchived) {
                    dispatch(slice.actions.archivedSourceCategory(response?.data));
                }
                if (data?.type === referralSummary && !data?.isArchived) {
                    dispatch(slice.actions.addReferralSummary(response?.data));
                }
                if (data?.type === referralSummary && data?.isArchived) {
                    dispatch(slice.actions.archivedReferralCategory(response?.data));
                }
                if (data?.type === archiveSummary) {
                    dispatch(slice.actions.addArchiveSummary(response?.data));
                }
            }
            else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

const getQuery = (data) => {
    let query = '';
    if (data?.groupByFilter) {
        query += '?aggregate[Count]=id&group_by[lead_treatments__treatment__treatment_item__treatment_category]';
    }
    if (data?.isNotBookedTable) {
        query += '&lead_treatments__status=NEW';
    }
    if (data?.startTime) {
        query += `&created_at__gte=${data?.startTime}`;
    }
    if (data?.endTime) {
        query += `&created_at__lte=${data?.endTime}`;
    }
    return query;
};

export function getReportsListing(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const response = await callApi(`/api/v1/practices/${getPracticeId()}/reports/leads/${getQuery(data)}`);
            if (response.status === 200) {
                if (data?.type === 'enquiry') {
                    dispatch(slice.actions.addEnquiryColumnData(response?.data));
                    return;
                }
                dispatch(slice.actions.addNotBookedColumnData(response?.data));
            }
            else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

const getReportAppointmentQuery = (data) => {
    let query = '?group_by[status]&aggregate[Count]=id';
    if (data?.startTime && data?.endTime) {
        query += `&datetime__gte=${data?.startTime}&datetime__lte=${data?.endTime}`;
    }
    if (data?.status && data.status !== 'undefined' && data.status !== 'null') {
        query += `&lead_treatment__status__in=${encodeURIComponent(data.status)}`;
    }
    if (data?.status__in && data.status__in !== 'undefined' && data.status__in !== 'null') {
        query += `&status__in=${encodeURIComponent(data.status__in)}`;
    }
    return query;
};

const getCounter = (response, key, statusIn) => {
    // If key and statusIn are provided, use them to calculate the count
    if (key && statusIn && isArray(response?.data)) {
        const statuses = statusIn.split(',');
        const count = response.data
            .filter(ele => statuses.includes(ele.status))
            .reduce((total, currentValue) => total + (currentValue.count_id || 0), 0);
        setCounter({ key }, count);
        return;
    }
    // Fallback to original behavior for non-widget counter uses
    if (isArray(response?.data) && response.data.length > 0) {
        response.data.forEach((ele) => {
            if (ele.status === 'BOOKED') {
                setCounter({ key: 'BOOKED' }, response.data.filter(ele => ele.status === 'BOOKED')
                    .reduce((total, currentValue) => total + (currentValue.count_id || 0), 0));
            }
            if (ele.status === 'ATTENDED') {
                setCounter({ key: 'ATTENDED' }, response.data.filter(ele => ele.status === 'ATTENDED')
                    .reduce((total, currentValue) => total + (currentValue.count_id || 0), 0));
            }
            if (ele.status === 'FAILED_TO_ATTEND') {
                setCounter({ key: 'FAILED_TO_ATTEND' }, response.data.filter(ele => ele.status === 'CANCELLED' || ele.status === 'FAILED_TO_ATTEND')
                    .reduce((total, currentValue) => total + (currentValue.count_id || 0), 0));
            }
        });
    }
};

export function getReportsAppointmentListing(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
                const response = await callApi(`/api/v1/practices/${getPracticeId()}/reports/appointments/${getReportAppointmentQuery(data)}`);
                if (response.status === 200) {
                    if (data.isWidgetCounter) {
                        // Pass key and status__in to use correct widget key
                        getCounter(response?.data, data.key, data.status__in);
                        return;
                    }
                    dispatch(slice.actions.addReportAppointmentData(response?.data));
                }
                else if (response.status === 403) {
                    // Silently ignore 403 for background widget counter preloads
                    if (!data.isWidgetCounter) {
                        dispatch(updateError({ message: response?.data?.detail || 'You do not have access to this feature', success: false }));
                    }
                }
                else {
                    dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
                }
            }

         catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function reportsUserListing(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const startTime = data?.startTime ? `?lead_treatment__created_at__gte=${data?.startTime}` : '';
            const endTime = data?.endTime ? `&lead_treatment__created_at__lte=${data?.endTime}` : '';
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/reports/practice-user/${startTime}${endTime}`);
            if (response.status === 200) {
                dispatch(slice.actions.addUserLeads(response?.data));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function reportUserLead(data) {
    return async () => {
        if (!hasFeatureAccess('analytics-reports')) return;
        try {
            const startTime = data?.startTime ? `?created_at__gte=${data?.startTime}` : '';
            const endTime = data?.endTime ? `&created_at__lte=${data?.endTime}` : '';
            const treatment = data?.treatment ? `&treatment__in=${data?.treatment}` : '';
            const status = data?.status ? `&status__in=${data?.status}` : '';
            const limit = data?.limit ? `&limit=${data?.limit}` : '';
            const offset = data?.offset === 0 || data?.offset ? `&offset=${data?.offset}` : '';
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/reports/practice-user/${data?.id}/leads/${startTime}${endTime}${treatment}${status}${limit}${offset}`);
            if (response.status === 200) {
                dispatch(slice.actions.addPatientList(response?.data));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function getScheduleSettings(practiceId, limit, offset,) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/report-schedule-settings/?limit=${limit}&offset=${offset}&ordering=-created_at`);
            if (response.status === 200) {
                dispatch(slice.actions.getScheduleSettingList(response?.data));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function addScheduleSettings(practiceId, data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/report-schedule-settings/`, 'post', data);
            if (response.status === 201) {
                handleClose();
                dispatch(updateError({ message: 'Schedule settings added successfully', success: true }));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function updateScheduleSettings(practiceId, id, data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/report-schedule-settings/${id}/`, 'patch', data);
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ message: 'Schedule settings update successfully', success: true }));

            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

export function deleteScheduleSettings(practiceId, id, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/report-schedule-settings/${id}/`, 'delete');
            if (response.status === 204) {
                handleClose();
                dispatch(updateError({ message: 'Schedule settings delete successfully', success: true }));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}

// One-off "send this schedule now" — used by the Send Test button.
// Options object form to avoid the default-param-last lint trap:
//   recipients (optional array) overrides the schedule's saved recepients
//     without persisting
//   onSuccess  (optional callback) fires when the 202 lands
// BE rate-limits this at 10/hour per user; the UI also guards against
// double-clicks via the isTestSending state.
export function testSendScheduleReport(practiceId, id, { recipients = null, onSuccess } = {}) {
    return async () => {
        dispatch(slice.actions.setIsTestSending(true));
        try {
            const body = recipients ? { recipients } : {};
            const response = await callApi(
                `/api/v1/practices/${practiceId}/report-schedule-settings/${id}/test-send/`,
                'post',
                body,
            );
            if (response.status === 202) {
                dispatch(updateError({ message: 'Test send queued — check your inbox shortly', success: true }));
                if (onSuccess) onSuccess();
            } else if (response.status === 429) {
                dispatch(updateError({ message: 'Too many test sends — try again in an hour', success: false }));
            } else {
                dispatch(updateError({
                    message: response?.data?.detail ?? response?.data?.message ?? 'Test send failed',
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Test send failed', success: false }));
        } finally {
            dispatch(slice.actions.setIsTestSending(false));
        }
    };
}

// Past CSV reports — list endpoint scoped to one schedule via the
// practice_report_settings filter. Returns DRF-paginated shape
// (results, count, next, previous); the Past Sends dialog reads
// .results and shows count in the header.
export function getGeneratedReports(practiceId, scheduleId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/generated-reports/?practice_report_settings=${scheduleId}&ordering=-created_at&limit=100`,
            );
            if (response.status === 200) {
                dispatch(slice.actions.setGeneratedReports({ scheduleId, data: response.data }));
            } else {
                dispatch(updateError({
                    message: response?.data?.detail ?? 'Failed to load past sends',
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Failed to load past sends', success: false }));
        }
    };
}

const handleReportsLoading = (state) => {
    if (state === false) {
        setTimeout(() => {
            dispatch(slice.actions.setReportsLoading(false));
        }, 500);
    }
    dispatch(slice.actions.setReportsLoading(state));
};

// Paginate /archived/ with archived_at desc ordering and stop as soon as we pass
// the start of the window. Backend silently ignores every date filter we tried
// (archived_at__gte/lte/range/*_after/*_before, created_at__gte/lte), so all date
// scoping has to happen client-side. See analyticsReports.js for the shared helper.
// Backend reality check (observed 2026-04-17 on production):
//   - /archived/ caps page size at 50
//   - All date filters are silently ignored (archived_at__*, created_at__*)
//   - `ordering=-archived_at` is ALSO ignored — the first ~1000 rows returned can
//     share an identical placeholder archived_at ("15-08-2024 08:24:24.311744Z"),
//     making any client-side "stop when older than range" heuristic unreliable.
//
// Pragmatic behaviour until backend gains real filtering:
//   - Count in the KPI card comes from /reports/archived-lead-summary/ (accurate).
//   - The popup falls back to the single /archived/ request the old code issued —
//     no client-side pagination, no date filter. The row list is therefore
//     "most recent archived leads" for the practice, not strictly the selected
//     period, but at least non-empty and ordered as the backend sees fit.
async function fetchArchivedPopupPage(practiceId, { limit, offset, archivedReason }) {
    const reasonFilter = archivedReason ? `&archived_reason__in=${encodeURIComponent(archivedReason)}` : '';
    const url = `/api/v1/practices/${practiceId}/archived/?is_archived=true&limit=${limit}&offset=${offset}${reasonFilter}`;
    return callApi(url);
}

export function getLead({ id, categoryId, status, limit, offset, order, orderId, source, startDate, endDate, treatment, referral, assignedTo, isArchived, useCreatedAt, archivedReason }) {
    return async () => {
        try {
            handleReportsLoading(true);
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const ordering = orderId ? orderingLead : '';

            // Archived popups: trust the backend's own paginated /archived/ endpoint.
            // Client-side date filtering is unreliable because /archived/ ignores
            // `ordering=-archived_at` and returns many rows sharing a placeholder
            // timestamp. The KPI card already shows the correct period count via
            // /reports/archived-lead-summary/ — the popup here surfaces the most
            // recent archives for context.
            if (isArchived || archivedReason) {
                const response = await fetchArchivedPopupPage(id, { limit, offset, archivedReason });
                if (response.status === 200) {
                    dispatch(slice.actions.getLeadList(response.data));
                } else {
                    dispatch(updateError({ message: response?.data?.detail || 'Failed to load archived leads', success: false }));
                }
                handleReportsLoading(false);
                return;
            }

            const customStartDate = `${startDate} 00:00:00`;
            const customEndDate = `${endDate} 23:59:00`;
            let date = '';
            if (startDate && endDate) {
                if (useCreatedAt) {
                    date = `&created_at__gte=${customStartDate}&created_at__lte=${customEndDate}`;
                } else {
                    date = `&state_transition_params={${(status && status !== 'All') ? `"state": "${status}",` : ''}"timestamp_gte": "${customStartDate}", "timestamp_lte": "${customEndDate}"}`;
                }
            }
            const leadSource = source?.length > 0 ? `&lead_source__type__in=${source}` : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const categoryQuery = categoryId ? `&lead_treatments__treatment__treatment_item__treatment_category=${categoryId}` : '';
            const treatmentQuery = treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const assignedToQuery = assignedTo ? `&assigned_to=${assignedTo}` : '';
            const archivedIncluded = '&is_archived_included=true';

            const baseEndpoint = `/api/v1/practices/${id}/leads/?limit=${limit}&offset=${offset}${categoryQuery}${ordering}${leadReferral}${leadSource}${date}${treatmentQuery}${assignedToQuery}${archivedIncluded}&${leadTableFields}`;
            const response = await callApi(baseEndpoint);
            if (response.status === 200) {
                dispatch(slice.actions.getLeadList(response.data));
                handleReportsLoading(false);
            } else {
                dispatch(updateError({ message: response?.data?.detail || 'Something went wrong', success: false }));
                handleReportsLoading(false);
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
            handleReportsLoading(false);
        }
    };
}

export function getLeadMonthlyStatus(practiceId, userId){
    return async () => {
        try {
            
            const response = await callApi(`/api/v1/practices/${practiceId}/users/${userId}/reports/lead-monthly-status/`);
            if (response.status === 200) {
                dispatch(slice.actions.addLeadMonthlyStatus(response?.data));
            } else {
                dispatch(updateError({ message: response?.data?.message ?? response?.data?.detail ?? 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false }));
        }
    };
}